import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/custom/Navbar";
import WeatherWidget from "@/components/custom/WeatherWidget";
import NeedCard from "@/components/custom/NeedCard";
import NeedsMap from "@/components/custom/NeedsMap";
import { SOSBanner } from "@/components/custom/SOSBanner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  RefreshCw, Filter, Package, Users, Truck, Home, Stethoscope,
  Map, List, Navigation, Heart,
} from "lucide-react";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const B = {
  border: `1px solid ${C.navy}08`,
  shadow: "none",
  shadowSm: "none",
  font: BODY,
  mono: MONO,
};

const NEED_TYPES = [
  { value: "all", label: "TODOS", icon: Filter },
  { value: "material", label: "MATERIAL", icon: Package },
  { value: "voluntarios", label: "VOLUNTARIOS", icon: Users },
  { value: "transporte", label: "TRANSPORTE", icon: Truck },
  { value: "acogida", label: "ACOGIDA", icon: Home },
  { value: "veterinario", label: "VETERINARIO", icon: Stethoscope },
];

const URGENCY_LEVELS = [
  { value: "all", label: "TODAS" },
  { value: "urgente", label: "URGENTE" },
  { value: "prioridad", label: "PRIORIDAD" },
  { value: "activo", label: "ACTIVO" },
  { value: "info", label: "INFO" },
];

export default function NeedsFeed() {
  const { user, setUser } = useAuth();
  const [needs, setNeeds] = useState([]);
  const [emergencies, setEmergencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [urgencyFilter, setUrgencyFilter] = useState("all");
  const [countryFilter, setCountryFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list");
  const [userLocation, setUserLocation] = useState(null);

  const fetchNeeds = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.append("status", "abierta");
    if (typeFilter !== "all") params.append("need_type", typeFilter);
    if (urgencyFilter !== "all") params.append("urgency", urgencyFilter);
    if (countryFilter !== "all") params.append("country", countryFilter);
    axios.get(`${API}/needs?${params.toString()}`).then((r) => setNeeds(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [typeFilter, urgencyFilter, countryFilter]);

  useEffect(() => {
    fetchNeeds();
    const emergencyParams = countryFilter !== "all" ? `?status=activo&country=${countryFilter}` : "?status=activo";
    axios.get(`${API}/emergencies${emergencyParams}`).then((r) => setEmergencies(r.data)).catch(() => {});
    const interval = setInterval(fetchNeeds, 30000);
    return () => clearInterval(interval);
  }, [fetchNeeds, countryFilter]);

  const handleSaveLocation = async (lat, lng) => {
    if (!user) return;
    const token = localStorage.getItem("ea_token");
    try {
      let locationName = "";
      try {
        const geo = await axios.get(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=es`);
        const addr = geo.data?.address;
        locationName = addr?.city || addr?.town || addr?.village || addr?.state || "";
      } catch {}
      await axios.put(`${API}/auth/profile`, { location_lat: lat, location_lng: lng, location_name: locationName }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      const updatedUser = { ...user, location_lat: lat, location_lng: lng, location_name: locationName };
      setUser(updatedUser);
      localStorage.setItem("ea_user", JSON.stringify(updatedUser));
      toast.success(locationName ? `${locationName}` : "Ubicación guardada");
    } catch {}
  };

  const handleGeolocate = () => {
    if (!navigator.geolocation) { toast.error("Tu navegador no soporta geolocalización"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude }; setUserLocation(loc); handleSaveLocation(loc.lat, loc.lng); toast.success("Ubicación actualizada"); },
      () => toast.error("No se pudo obtener tu ubicación"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="min-h-screen" data-testid="needs-feed-page" style={{ background: "#f8f7f4", fontFamily: B.font }}>
      <Navbar />
      <div className="pt-24"><SOSBanner /></div>

      <div className="pt-28 pb-16 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-3 space-y-4">
            <WeatherWidget compact userLat={user?.location_lat} userLng={user?.location_lng} userName={user?.location_name} />

            {user && (
              <button onClick={handleGeolocate} data-testid="sidebar-geolocate-btn"
                className="w-full py-2.5 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-all duration-150 hover:-translate-y-0.5"
                style={{ fontFamily: B.mono, background: "#fff", color: "#686af3", border: "1px solid #686af3", borderRadius: 4 }}
              >
                <Navigation className="w-4 h-4" /> ACTUALIZAR MI UBICACIÓN
              </button>
            )}

            <div className="bg-white p-5" style={{ border: B.border, boxShadow: B.shadowSm }}>
              <h3 className="text-sm font-bold tracking-tight mb-4 flex items-center gap-2" style={{ color: "#2D3561" }}>
                <div className="w-6 h-6 flex items-center justify-center" style={{ background: "#686af3", border: "1px solid #2D3561" }}>
                  <Filter className="w-3 h-3 text-white" />
                </div>
                FILTROS
              </h3>
              <div className="space-y-3">
                {/* Country Filter */}
                <div>
                  <label className="text-[10px] font-bold tracking-widest mb-1.5 block" style={{ fontFamily: B.mono, color: "#f64e0e" }}>PAÍS</label>
                  <div className="flex gap-1">
                    {[
                      { code: "all", label: "TODOS", title: "Todos" },
                      { code: "ES", label: "ES", title: "España" },
                      { code: "EC", label: "EC", title: "Ecuador" },
                    ].map((c) => (
                      <button
                        key={c.code}
                        onClick={() => setCountryFilter(c.code)}
                        title={c.title}
                        style={{ fontFamily: B.mono, color: countryFilter === c.code ? "#fff" : "#2D3561" }}
                        className="flex-1 py-2 text-[10px] font-bold tracking-wider transition-all"
                        style={{
                          background: countryFilter === c.code ? "#2D3561" : "#f8f7f4",
                          border: B.border, borderRadius: 4,
                        }}
                        data-testid={`country-filter-${c.code}`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold tracking-widest mb-1.5 block" style={{ fontFamily: B.mono, color: "#a88efb" }}>TIPO</label>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger style={{ border: B.border, borderRadius: 4, borderRadius: 4, fontFamily: B.mono, fontSize: "0.75rem" }} data-testid="filter-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{ borderRadius: 4, border: B.border }}>
                      {NEED_TYPES.map((t) => <SelectItem key={t.value} value={t.value} style={{ fontFamily: B.mono }}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] font-bold tracking-widest mb-1.5 block" style={{ fontFamily: B.mono, color: "#a88efb" }}>URGENCIA</label>
                  <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
                    <SelectTrigger style={{ border: B.border, borderRadius: 4, borderRadius: 4, fontFamily: B.mono, fontSize: "0.75rem" }} data-testid="filter-urgency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{ borderRadius: 4, border: B.border }}>
                      {URGENCY_LEVELS.map((u) => <SelectItem key={u.value} value={u.value} style={{ fontFamily: B.mono }}>{u.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Main feed */}
          <div className="lg:col-span-9">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: "#2D3561" }}>
                  NECESIDADES <span style={{ color: "#686af3" }}>ACTIVAS</span>
                </h1>
                <p className="text-xs mt-1" style={{ fontFamily: B.mono, color: "#a88efb" }}>
                  // {needs.length} necesidad{needs.length !== 1 ? "es" : ""} encontrada{needs.length !== 1 ? "s" : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex" style={{ border: B.border, borderRadius: 4 }}>
                  {[{ id: "list", icon: List }, { id: "map", icon: Map }].map((v) => (
                    <button key={v.id} onClick={() => setViewMode(v.id)} data-testid={`view-${v.id}-btn`}
                      className="w-9 h-9 flex items-center justify-center transition-colors"
                      style={{ background: viewMode === v.id ? "#686af3" : "#fff", color: viewMode === v.id ? "#fff" : "#2D3561" }}
                    >
                      <v.icon className="w-4 h-4" />
                    </button>
                  ))}
                </div>
                <button onClick={fetchNeeds} data-testid="refresh-needs-btn"
                  className="w-9 h-9 flex items-center justify-center" style={{ border: B.border, borderRadius: 4, background: "#fff" }}
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} style={{ color: "#686af3" }} />
                </button>
              </div>
            </div>

            {/* Type filter badges */}
            <div className="flex flex-wrap gap-2 mb-6">
              {NEED_TYPES.map((t) => (
                <button key={t.value} onClick={() => setTypeFilter(t.value)} data-testid={`filter-badge-${t.value}`}
                  className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 transition-all duration-150"
                  style={{
                    fontFamily: B.mono,
                    background: typeFilter === t.value ? "#686af3" : "#fff",
                    color: typeFilter === t.value ? "#fff" : "#2D3561",
                    border: typeFilter === t.value ? `1px solid ${C.navy}` : `1px solid ${C.navy}12`,
                    boxShadow: typeFilter === t.value ? "3px 3px 0px #2D3561" : "none",
                  }}
                >
                  <t.icon className="w-3 h-3" />{t.label}
                </button>
              ))}
            </div>

            {viewMode === "map" && (
              <div className="mb-6" style={{ border: B.border }}><NeedsMap needs={needs} emergencies={emergencies} onOfferSent={fetchNeeds} /></div>
            )}

            {viewMode === "list" && (
              <>
                {loading && needs.length === 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="bg-white p-5 animate-pulse" style={{ border: B.border }}>
                        <div className="h-4 bg-[#f8f7f4] w-3/4 mb-3" />
                        <div className="h-3 bg-[#f8f7f4] w-1/2 mb-2" />
                        <div className="h-3 bg-[#f8f7f4] w-full" />
                      </div>
                    ))}
                  </div>
                ) : needs.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {needs.map((need) => <NeedCard key={need.need_id} need={need} onOfferSent={fetchNeeds} />)}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-white" style={{ border: B.border, boxShadow: B.shadow }}>
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center" style={{ background: "#686af3", border: B.border }}>
                      <Heart className="w-7 h-7 text-white" />
                    </div>
                    <p className="text-lg font-bold" style={{ color: "#2D3561" }}>NO HAY NECESIDADES ACTIVAS</p>
                    <p className="text-xs mt-1" style={{ fontFamily: B.mono, color: "#a88efb" }}>// TODAS LAS NECESIDADES ESTÁN CUBIERTAS</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
